// Task #197 — redesigned "Team performance — Assessments" list. The
// previous design rendered one fat ``TeamAssessmentCard`` per team
// stacked in scope-order, which forced managers to read every chart
// to find the team in trouble. This list instead renders one
// collapsed row per team — severity dot, headline measure, delta vs
// prior — sorted worst-first by default, with the existing per-team
// charts available behind a one-click expand. Reuses the existing
// trend / by-assessment charts on expand so we don't fork chrome.
import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, ArrowRight } from "lucide-react";

import { api } from "@/lib/api";
import type {
  AgentMeasureRow,
  AnalyticsTeamOption,
  AssessmentAggregate,
  TeamTriageRow,
} from "@/lib/api";
import { agentSlug } from "@/lib/agents.helpers";

import { agentProfileHref } from "@/lib/router";
import {
  cohortAveragePerLabel,
  cohortAverageRate,
  formatDeltaPp,
  isLowSample,
  LOW_SAMPLE_THRESHOLD,
  overallDetectionRate,
  severityTier,
  type SeverityTier,
  triageSort,
  type TriageSort,
  worstMeasure,
} from "@/lib/triage";
import { MeasureBarChart, type MeasureFocus } from "@/components/MeasureBarChart";
import {
  TeamCallsByAssessmentStackedBar,
  TeamTrendLineChart,
} from "@/components/AssessmentCharts";
import { FocusedMeasureAgentPanel } from "@/components/FocusedMeasureAgentPanel";
import { Tooltip } from "@/components/Tooltip";
import type { AgentDrillNav } from "@/views/TeamsDashboardView";
import { summariseMeasures } from "@/views/AgentProfileView.helpers";

import type { TimeRange, RangeMode } from "@/lib/api";

// Pre-tailwind class names so jsdom-based tests can assert the
// severity dot without depending on computed colour values.
const SEVERITY_DOT: Record<SeverityTier, string> = {
  red: "bg-destructive",
  amber: "bg-yellow-500",
  green: "bg-emerald-500",
  neutral: "bg-muted-foreground/40",
};

function SeverityDot({
  tier,
  label,
  title,
}: {
  tier: SeverityTier;
  label: string;
  title?: string;
}) {
  return (
    <span
      role="img"
      aria-label={label}
      title={title}
      data-severity={tier}
      className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${SEVERITY_DOT[tier]}`}
    />
  );
}

// Task #215 — copy lives next to the threshold constant so a future
// tuning pass to LOW_SAMPLE_THRESHOLD updates the tooltip too.
export const LOW_SAMPLE_TOOLTIP = `Fewer than ${LOW_SAMPLE_THRESHOLD} graded calls in this period — percentages may not be representative.`;

export function TeamTriageList({
  teams,
  rangeFrom,
  rangeTo,
  priorFrom,
  priorTo,
  timeRange,
  rangeMode,
  assessmentId,
  agentId = null,
  onAgentDrillNav,
}: {
  teams: AnalyticsTeamOption[];
  rangeFrom: string | null;
  rangeTo: string | null;
  // ``null`` when the active range has no defined prior (e.g.
  // All-time) — the list still renders, just without prior-period
  // comparisons.
  priorFrom: string | null;
  priorTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  assessmentId: string | null;
  // Task #302 — global Agent filter from the Teams view.
  // ``null`` ⇒ "All agents". When set every triage row's metrics,
  // expanded charts, and agent drill are scoped to that agent.
  agentId?: string | null;
  onAgentDrillNav: AgentDrillNav;
}) {
  const [sort, setSort] = useState<TriageSort>("triage");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const hasPriorWindow = !!(priorFrom && priorTo);

  // Pass the caller-selected team scope through to the backend so a
  // selected team in the Teams view narrows the triage list
  // instead of showing every actor-visible team. Stable key so React
  // Query dedupes equal selections.
  const teamIdsKey = useMemo(
    () => teams.map((t) => t.id).slice().sort().join(","),
    [teams],
  );
  const triageQ = useQuery<TeamTriageRow[]>({
    queryKey: [
      "teams-triage",
      rangeFrom,
      rangeTo,
      priorFrom,
      priorTo,
      assessmentId,
      teamIdsKey,
      agentId,
    ],
    queryFn: () =>
      api.teamsTriage({
        from: rangeFrom!,
        to: rangeTo!,
        prior_from: priorFrom,
        prior_to: priorTo,
        assessment_id: assessmentId,
        team_ids: teams.map((t) => t.id),
        agent_id: agentId,
      }),
    enabled: !!rangeFrom && !!rangeTo,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  // Reused by the expand body so we don't fork the by-assessment chart
  // chrome. Same key the legacy ``TeamAssessmentCard`` used, so React
  // Query dedupes if anything else mounts both surfaces.
  const byAssessmentQ = useQuery<AssessmentAggregate[]>({
    queryKey: ["by-assessment-aggregates", rangeFrom, rangeTo, agentId],
    queryFn: () =>
      api.listByAssessmentAggregates({
        from: rangeFrom!,
        to: rangeTo!,
        agent_id: agentId,
      }),
    enabled: !!rangeFrom && !!rangeTo,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  // Names from the analytics team list win over the triage rows so we
  // honour any future renames before the triage cache invalidates.
  const teamNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of teams) m.set(t.id, t.name);
    return m;
  }, [teams]);

  const rows = useMemo<TeamTriageRow[]>(() => {
    const raw = triageQ.data ?? [];
    // Hydrate with a friendlier display name if the analytics list
    // already has one cached locally. (Scope filtering happens on
    // the server via ``team_ids`` so a selected team in the
    // Teams view doesn't get bypassed.)
    return raw.map((r) => ({
      ...r,
      team_name: teamNameById.get(r.team_id) ?? r.team_name,
    }));
  }, [triageQ.data, teamNameById]);

  const teamAvgPerLabel = useMemo(
    () => cohortAveragePerLabel(rows),
    [rows],
  );
  const teamAvgPerLabelRows = useMemo(() => {
    // MeasureBarChart wants AggregateRow shape. Derive from any team's
    // per_label as the canonical (measure_id, label_name) catalogue.
    const seen = new Map<
      string,
      {
        measure_id: string;
        measure_label: string;
        label_name: string;
        detection_rate: number;
        calls: number;
      }
    >();
    for (const t of rows) {
      for (const r of t.per_label) {
        const key = `${r.measure_id}::${r.label_name}`;
        if (seen.has(key)) continue;
        seen.set(key, {
          measure_id: r.measure_id,
          measure_label: r.measure_label,
          label_name: r.label_name,
          detection_rate: teamAvgPerLabel.get(key) ?? 0,
          calls: 0,
        });
      }
    }
    return Array.from(seen.values());
  }, [rows, teamAvgPerLabel]);

  const cohortAvg = useMemo(() => cohortAverageRate(rows), [rows]);

  const sortedRows = useMemo(() => {
    if (sort === "triage") return triageSort(rows);
    if (sort === "name")
      return rows.slice().sort((a, b) => a.team_name.localeCompare(b.team_name));
    // volume
    return rows
      .slice()
      .sort((a, b) =>
        a.call_count === b.call_count
          ? a.team_name.localeCompare(b.team_name)
          : b.call_count - a.call_count,
      );
  }, [rows, sort]);

  const toggle = (teamId: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });

  if (teams.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">No teams in your scope.</div>
    );
  }

  if (triageQ.isLoading && !triageQ.data) {
    return (
      <div
        className="bg-muted/40 rounded h-24 animate-pulse"
        aria-hidden="true"
        data-testid="teams-triage-loading"
      />
    );
  }
  if (triageQ.error && !triageQ.data) {
    return (
      <div className="text-sm text-destructive flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {(triageQ.error as { message?: string })?.message ||
            "Failed to load team triage."}
        </span>
        <button
          type="button"
          onClick={() => triageQ.refetch()}
          className="ml-auto inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40"
        >
          Retry
        </button>
      </div>
    );
  }

  if (sortedRows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No assessments graded in this period.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Sort teams"
        className="inline-flex items-center gap-1"
      >
        {(
          [
            { id: "triage" as const, label: "Triage" },
            { id: "name" as const, label: "Name" },
            { id: "volume" as const, label: "Volume" },
          ] as const
        ).map((opt) => {
          const active = sort === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              aria-pressed={active}
              onClick={() => setSort(opt.id)}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                active
                  ? "bg-primary/10 text-primary border border-primary/30"
                  : "bg-card text-muted-foreground border border-border hover:bg-muted/40"
              }`}
            >
              Sort: {opt.label}
            </button>
          );
        })}
      </div>
      <ul className="space-y-2" role="list">
        {sortedRows.map((row) => {
          const tier = severityTier(row, cohortAvg);
          const overall = overallDetectionRate(row.per_label);
          const worst = worstMeasure(row.per_label, row.prior_per_label);
          const isOpen = expanded.has(row.team_id);
          const lowSample = isLowSample(row.call_count);
          return (
            <li
              key={row.team_id}
              className="rounded border border-border bg-card"
              data-testid="team-triage-row"
              data-team-id={row.team_id}
            >
              <TriageRowHeader
                row={row}
                tier={tier}
                overall={overall}
                worst={worst}
                isOpen={isOpen}
                lowSample={lowSample}
                trendDelta={computeTrendDelta(row)}
                hasPriorWindow={hasPriorWindow}
                onToggle={() => toggle(row.team_id)}
              />
              {isOpen ? (
                <ExpandedTeamBody
                  row={row}
                  teamAvgRows={teamAvgPerLabelRows}
                  rangeFrom={rangeFrom}
                  rangeTo={rangeTo}
                  priorFrom={priorFrom}
                  priorTo={priorTo}
                  timeRange={timeRange}
                  rangeMode={rangeMode}
                  assessmentId={assessmentId}
                  agentId={agentId}
                  byAssessmentAggregates={byAssessmentQ.data}
                  byAssessmentLoading={byAssessmentQ.isLoading}
                  byAssessmentError={
                    byAssessmentQ.error
                      ? (byAssessmentQ.error as { message?: string })
                      : null
                  }
                  onByAssessmentRetry={() => byAssessmentQ.refetch()}
                  onAgentDrillNav={onAgentDrillNav}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Tooltip copy is centralised so the same wording is reused by the
// tests. Keeping each string short keeps the popup compact for the
// dense triage row.
export const TRIAGE_TOOLTIP_COPY = {
  severity: {
    red: "Red: overall detection well below the floor — needs attention.",
    amber:
      "Amber: overall detection below target, or a measure dropped sharply vs the prior period.",
    green: "Green: overall detection at or above target this period.",
    neutral:
      "Grey: nothing has been graded in this period, so no severity can be calculated.",
  },
  overall:
    "Average score across all graded calls for this team's assigned assessment in the selected period.",
  namedMeasureRate:
    "Weakest measure for this team on the assigned assessment. The number is the current detection rate (no prior period to compare).",
  namedMeasureDelta:
    "Weakest measure for this team on the assigned assessment. The number is the change vs the prior period (in percentage points).",
  callCount: "Number of graded calls feeding these numbers.",
  noGradedCalls:
    "No calls were graded for this team in the selected period. This usually means no calls happened in range, or grading hasn't run yet.",
} as const;

export { TriageRowHeader };

// Task #238 — compute the overall%-points delta vs prior for a team
// triage row, mirroring the Assessment performance card's trend chip
// on the single-team view. Returns ``null`` when the row has no
// prior-period data (e.g. All-time, or the prior window had no
// graded calls), in which case the chip renders an em-dash slot.
function computeTrendDelta(row: TeamTriageRow): number | null {
  if (!row.prior_call_count || row.prior_per_label.length === 0) return null;
  const cur = summariseMeasures(row.per_label);
  const prior = summariseMeasures(row.prior_per_label);
  if (!cur || !prior) return null;
  return cur.overallPct - prior.overallPct;
}

function TeamTrendChip({ delta }: { delta: number | null }) {
  if (delta === null || !Number.isFinite(delta)) {
    return (
      <span
        className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted/40 text-muted-foreground shrink-0"
        title="No prior-period comparison available"
        data-testid="team-trend-chip"
        data-trend="none"
      >
        — vs prior
      </span>
    );
  }
  const rounded = Math.round(delta);
  if (rounded === 0) {
    return (
      <span
        className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted/40 text-muted-foreground shrink-0"
        data-testid="team-trend-chip"
        data-trend="flat"
      >
        ↔ 0 pts
      </span>
    );
  }
  const up = rounded > 0;
  const cls = up
    ? "bg-primary/15 text-primary"
    : "bg-destructive/15 text-destructive";
  const arrow = up ? "↑" : "↓";
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium shrink-0 ${cls}`}
      title={`${arrow} ${Math.abs(rounded)} pts vs prior period`}
      data-testid="team-trend-chip"
      data-trend={up ? "up" : "down"}
    >
      {arrow} {Math.abs(rounded)} pts
    </span>
  );
}

function TriageRowHeader({
  row,
  tier,
  overall,
  worst,
  isOpen,
  lowSample,
  trendDelta,
  hasPriorWindow,
  onToggle,
}: {
  row: TeamTriageRow;
  tier: SeverityTier;
  overall: number;
  worst: ReturnType<typeof worstMeasure>;
  isOpen: boolean;
  // Task #215 — when true the headline numbers are muted, a "Low
  // sample" chip is rendered, and the severity / call-count
  // tooltips append the low-sample caveat so users don't trust the
  // percentages too far.
  lowSample: boolean;
  // Task #238 — overall%-points delta vs prior, mirroring the
  // single-team Assessment performance card's trend chip. The chip
  // is rendered when ``hasPriorWindow`` is true; ``trendDelta`` may
  // still be ``null`` (no prior data for this team) in which case
  // the chip shows an em-dash. Both props are optional so existing
  // tests that render the header in isolation don't need updating.
  trendDelta?: number | null;
  hasPriorWindow?: boolean;
  onToggle: () => void;
}) {
  // The whole row stays click-to-expand, but we use a div +
  // ``role="button"`` rather than a native ``<button>`` so the inner
  // segments can be focusable spans (which would be invalid HTML
  // inside a <button>). Keyboard activation mirrors what the browser
  // does for a real button.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      // Don't hijack space/enter when focus is on a child segment —
      // the segment owns the keypress (e.g. an interactive tooltip
      // trigger). The row only handles activation on its own surface.
      if (e.target !== e.currentTarget) return;
      e.preventDefault();
      onToggle();
    }
  };
  const showDelta = !!worst && worst.delta !== null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={onKeyDown}
      aria-expanded={isOpen}
      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] cursor-pointer"
    >
      <ChevronRight
        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
          isOpen ? "rotate-90" : ""
        }`}
        aria-hidden="true"
      />
      <Tooltip
        content={
          lowSample
            ? `${TRIAGE_TOOLTIP_COPY.severity[tier]} ${LOW_SAMPLE_TOOLTIP}`
            : TRIAGE_TOOLTIP_COPY.severity[tier]
        }
        data-testid="triage-tooltip-severity"
      >
        <span
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
          className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
        >
          <SeverityDot
            tier={tier}
            label={`Severity ${tier} for ${row.team_name}`}
          />
        </span>
      </Tooltip>
      <span className="text-sm font-medium text-foreground truncate flex-1">
        {row.team_name}
      </span>
      {row.call_count === 0 ? (
        <Tooltip
          content={TRIAGE_TOOLTIP_COPY.noGradedCalls}
          data-testid="triage-tooltip-no-graded"
        >
          <span
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") e.stopPropagation();
            }}
            className="text-xs text-muted-foreground italic focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            No graded calls
          </span>
        </Tooltip>
      ) : (
        <>
          <span
            className={`text-xs tabular-nums inline-flex items-center gap-1 ${
              lowSample ? "text-muted-foreground/60" : "text-muted-foreground"
            }`}
            data-testid="triage-headline"
            data-low-sample={lowSample ? "true" : undefined}
          >
            <Tooltip
              content={TRIAGE_TOOLTIP_COPY.overall}
              data-testid="triage-tooltip-overall"
            >
              <span
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    e.stopPropagation();
                }}
                className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                Overall {Math.round(overall * 100)}%
              </span>
            </Tooltip>
            {worst ? (
              <>
                <span aria-hidden="true">·</span>
                <Tooltip
                  content={
                    showDelta
                      ? TRIAGE_TOOLTIP_COPY.namedMeasureDelta
                      : TRIAGE_TOOLTIP_COPY.namedMeasureRate
                  }
                  data-testid="triage-tooltip-named-measure"
                >
                  <span
                    tabIndex={0}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        e.stopPropagation();
                    }}
                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    {worst.row.label_name}{" "}
                    {showDelta
                      ? formatDeltaPp(worst.delta)
                      : `${Math.round(worst.row.detection_rate * 100)}%`}
                  </span>
                </Tooltip>
              </>
            ) : null}
          </span>
          {hasPriorWindow ? (
            <span
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
            >
              <TeamTrendChip delta={trendDelta ?? null} />
            </span>
          ) : null}
          <Tooltip
            content={
              lowSample
                ? `${TRIAGE_TOOLTIP_COPY.callCount} ${LOW_SAMPLE_TOOLTIP}`
                : TRIAGE_TOOLTIP_COPY.callCount
            }
            data-testid="triage-tooltip-calls"
          >
            <span
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") e.stopPropagation();
              }}
              className="text-xs text-muted-foreground tabular-nums shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              {row.call_count.toLocaleString()}{" "}
              {row.call_count === 1 ? "call" : "calls"}
            </span>
          </Tooltip>
          {lowSample ? (
            <Tooltip
              content={LOW_SAMPLE_TOOLTIP}
              data-testid="triage-tooltip-low-sample"
            >
              <span
                tabIndex={0}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    e.stopPropagation();
                }}
                className="text-[10px] font-medium uppercase tracking-wide rounded border border-border bg-muted/40 text-muted-foreground px-1.5 py-0.5 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="low-sample-chip"
              >
                Low sample
              </span>
            </Tooltip>
          ) : null}
        </>
      )}
    </div>
  );
}

function ExpandedTeamBody({
  row,
  teamAvgRows,
  rangeFrom,
  rangeTo,
  priorFrom,
  priorTo,
  timeRange,
  rangeMode,
  assessmentId,
  agentId = null,
  byAssessmentAggregates,
  byAssessmentLoading,
  byAssessmentError,
  onByAssessmentRetry,
  onAgentDrillNav,
}: {
  row: TeamTriageRow;
  teamAvgRows: import("@/lib/api").AggregateRow[];
  rangeFrom: string | null;
  rangeTo: string | null;
  // Task #320 — threaded through so the focused-measure agent panel
  // can render prior-period Δpp deltas matching By assessment.
  priorFrom: string | null;
  priorTo: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  assessmentId: string | null;
  // Task #302 — global Agent filter from the Teams view, used
  // to scope the team trend-line and the focused agent drill query
  // when set.
  agentId?: string | null;
  byAssessmentAggregates: AssessmentAggregate[] | undefined;
  byAssessmentLoading: boolean;
  byAssessmentError: { message?: string } | null;
  onByAssessmentRetry: () => void;
  onAgentDrillNav: AgentDrillNav;
}) {
  const [focused, setFocused] = useState<MeasureFocus | null>(null);
  // Task #320 — only run the local unfocused "Agents · overall"
  // query when no measure bar is selected. The focused branch is
  // owned by the shared ``FocusedMeasureAgentPanel``, which has its
  // own current + prior queries.
  const agentsQ = useQuery<AgentMeasureRow[]>({
    queryKey: [
      "team-agents-by-measure",
      row.team_id,
      rangeFrom,
      rangeTo,
      assessmentId,
      null,
      null,
    ],
    queryFn: () =>
      api.teamAgentsByMeasure(row.team_id, {
        from: rangeFrom!,
        to: rangeTo!,
        assessment_id: assessmentId,
        measure_id: null,
        label_name: null,
      }),
    enabled: !!rangeFrom && !!rangeTo && !focused,
    refetchOnWindowFocus: false,
  });
  const teamRate = overallDetectionRate(row.per_label);

  return (
    <div className="px-3 pb-3 pt-1 space-y-4 border-t border-border">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TeamCallsByAssessmentStackedBar
          teamId={row.team_id}
          teamName={row.team_name}
          aggregates={byAssessmentAggregates}
          isLoading={byAssessmentLoading}
          error={byAssessmentError}
          onRetry={onByAssessmentRetry}
        />
        <TeamTrendLineChart
          teamId={row.team_id}
          assessmentId={assessmentId}
          measureId={focused?.measure_id ?? null}
          labelName={focused?.label_name ?? null}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          agentId={agentId}
        />
      </div>
      <MeasureBarChart
        rows={row.per_label}
        callCount={row.call_count}
        priorRows={row.prior_per_label}
        teamAverageRows={teamAvgRows}
        focused={focused}
        onFocus={setFocused}
        groupStorageKey={`team-triage:assessment:${assessmentId ?? "any"}:team:${row.team_id}`}
      />
      {focused ? (
        <FocusedMeasureAgentPanel
          teamId={row.team_id}
          focused={focused}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          priorFrom={priorFrom}
          priorTo={priorTo}
          timeRange={timeRange}
          rangeMode={rangeMode}
          assessmentId={assessmentId}
          agentScopeId={agentId}
          onAgentDrillNav={onAgentDrillNav}
          onClear={() => setFocused(null)}
        />
      ) : (
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Agents · overall
          </div>
        </div>
        {agentsQ.isLoading ? (
          <div
            className="bg-muted/40 rounded h-12 animate-pulse"
            aria-hidden="true"
          />
        ) : agentsQ.error ? (
          <div className="text-xs text-destructive">
            {(agentsQ.error as { message?: string })?.message ||
              "Failed to load agents."}
          </div>
        ) : (() => {
          // Task #302 — when the global Agent filter is set, only show
          // that agent's row so the drill list stays consistent with
          // the scoped triage headline.
          const scopedAgents = agentId
            ? (agentsQ.data ?? []).filter((a) => a.agent_id === agentId)
            : (agentsQ.data ?? []);
          if (scopedAgents.length === 0) {
            return (
              <div className="text-xs text-muted-foreground italic">
                No agents matched.
              </div>
            );
          }
          return (
          <div className="space-y-1">
            {scopedAgents.map((a) => {
              const label = a.agent_name ?? "Unknown agent";
              const slug = agentSlug({
                full_name: a.agent_name,
                email: null,
                agent_id: a.agent_id,
              });
              const pct = Math.round(a.detection_rate * 100);
              const delta = a.detection_rate - teamRate;
              // Task #391 — open the agent profile in a new tab via a
              // real anchor so the dashboard tab keeps its place and
              // hover-URL / middle-click / Cmd-click work natively.
              const href = agentProfileHref(slug, a.agent_id, {
                slice: null,
                team: row.team_id,
                timeRange,
                rangeMode,
              });
              return (
                <a
                  key={a.agent_id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="triage-agent-row"
                  className="w-full flex items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                >
                  <span className="text-sm text-foreground truncate flex-1">
                    {label}
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {pct}%{" "}
                    <span
                      className={
                        delta >= 0
                          ? "text-emerald-600"
                          : "text-destructive"
                      }
                    >
                      ({formatDeltaPp(delta) || "0pp"})
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {a.call_count.toLocaleString()}{" "}
                    {a.call_count === 1 ? "call" : "calls"}
                  </span>
                  <ArrowRight
                    className="h-4 w-4 text-muted-foreground shrink-0"
                    aria-hidden="true"
                  />
                </a>
              );
            })}
          </div>
          );
        })()}
      </div>
      )}
    </div>
  );
}
